import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { MouseEvent as ReactMouseEvent, RefObject } from "react";

/**
 * Compress-to-fit + fisheye lens controller for a tick strip (the frame
 * ruler and the wall index).
 *
 * Measures the vertical space the strip actually receives (the strip root
 * declares its natural height and lets the rail's flex layout squeeze it,
 * observed via ResizeObserver). When the natural height exceeds the
 * available space, every slot compresses uniformly so all frames stay
 * visible — nothing clips.
 *
 * While compressed, mousemove applies a Gaussian fisheye lens: slots near
 * the cursor grow back toward their natural height, distant slots shrink
 * to compensate, so the total strip height never changes. Heights are
 * mutated directly on the DOM inside a requestAnimationFrame throttle —
 * no React re-render per mousemove. The focused slot is flagged with
 * `data-lens-hot` (styled via group-data variants) and reported through
 * `onFocusChange` so the component can show its tooltip.
 *
 * Reference design: docs/specs/2026-06-12-left-rail-fisheye-overflow.html
 */

/* ── Constants ── */
/** Natural per-tick slot height — today's hit area, unchanged. */
export const NATURAL_SLOT_H = 14;
/** Natural group-separator slot height (1px line + 3px space each side). */
export const SEP_SLOT_H = 7;
/** Lens falloff width, in slots. */
const SIGMA_SLOTS = 3.2;
/** Strip root vertical padding (py-1 → 4px top + 4px bottom). */
export const STRIP_PADDING_Y = 8;
const STRIP_PADDING_TOP = 4;
/** Hard floor for lensed slot heights. */
const MIN_LENS_SLOT_H = 1;
/** Sub-pixel measurement noise tolerance. */
const EPSILON = 0.5;

export interface LensSlot {
  isSeparator: boolean;
  /** Frame node id for tick slots, null for separators. */
  nodeId: string | null;
}

function contentBoxHeight(el: HTMLElement, borderBoxHeight: number): number {
  const style = getComputedStyle(el);
  const paddingY =
    (Number.parseFloat(style.paddingTop) || 0) +
    (Number.parseFloat(style.paddingBottom) || 0);
  return Math.max(0, borderBoxHeight - paddingY);
}

interface TickStripLens {
  /** Attach to the strip root element (the measured, squeezable box). */
  stripRef: RefObject<HTMLDivElement | null>;
  /** Attach to each slot element, by flat slot index. */
  registerSlot: (index: number, el: HTMLElement | null) => void;
  /** True when slots are compressed below natural height. */
  compressed: boolean;
  /** Natural content height (slots only, without strip padding). */
  naturalHeight: number;
  /** Base (uniform) height per slot for the current layout. */
  slotHeights: number[];
  /** Node id of the lens-focused tick, null when the lens is inactive. */
  focusNodeIdRef: RefObject<string | null>;
  /** Strip-level mousemove handler — drives the lens while compressed. */
  handleMouseMove: (event: ReactMouseEvent) => void;
  /** Eases slots back to their uniform heights and clears the focus. */
  resetLens: () => void;
}

export function useTickStripLens(
  slots: LensSlot[],
  onFocusChange: (nodeId: string | null) => void,
): TickStripLens {
  const stripRef = useRef<HTMLDivElement | null>(null);
  const slotElsRef = useRef<(HTMLElement | null)[]>([]);
  const slotsRef = useRef(slots);
  const basesRef = useRef<number[]>([]);
  const compressedRef = useRef(false);
  const focusNodeIdRef = useRef<string | null>(null);
  const pointerYRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const onFocusChangeRef = useRef(onFocusChange);

  useEffect(() => {
    onFocusChangeRef.current = onFocusChange;
  }, [onFocusChange]);

  const [availableHeight, setAvailableHeight] = useState<number | null>(null);

  const naturalHeight = useMemo(
    () =>
      slots.reduce(
        (sum, slot) => sum + (slot.isSeparator ? SEP_SLOT_H : NATURAL_SLOT_H),
        0,
      ),
    [slots],
  );

  const compressed =
    availableHeight !== null &&
    slots.length > 0 &&
    naturalHeight - availableHeight > EPSILON;

  const slotHeights = useMemo(() => {
    if (!compressed || availableHeight === null) {
      return slots.map((slot) =>
        slot.isSeparator ? SEP_SLOT_H : NATURAL_SLOT_H,
      );
    }
    const fitBase = availableHeight / slots.length;
    return slots.map((slot) =>
      slot.isSeparator ? Math.min(SEP_SLOT_H, fitBase) : fitBase,
    );
  }, [slots, compressed, availableHeight]);

  useLayoutEffect(() => {
    slotsRef.current = slots;
    basesRef.current = slotHeights;
    compressedRef.current = compressed;
    slotElsRef.current.length = slots.length;
  }, [slots, slotHeights, compressed]);

  /* ── Measure the ruler lane that owns the strip's available space ── */
  useLayoutEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const measureTarget = el.parentElement ?? el;
    const targetIsStrip = measureTarget === el;
    const measure = (height: number) => {
      // A transient 0px report while the rail is mounting/transitioning would
      // collapse every slot into one line. Keep the last usable measurement
      // until the flex lane has a real size.
      if (height < EPSILON) return;
      setAvailableHeight((prev) =>
        prev !== null && Math.abs(prev - height) < EPSILON ? prev : height,
      );
    };
    // The parent rail lane is the constrained box between top utilities and
    // the fit-view controls. Measure its content box, then subtract the
    // strip's own vertical padding so slot heights sum to the usable lane.
    const initialContentHeight = contentBoxHeight(
      measureTarget,
      measureTarget.clientHeight,
    );
    measure(
      targetIsStrip
        ? initialContentHeight
        : initialContentHeight - STRIP_PADDING_Y,
    );
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry) {
        measure(
          targetIsStrip
            ? entry.contentRect.height
            : entry.contentRect.height - STRIP_PADDING_Y,
        );
      }
    });
    observer.observe(measureTarget);
    return () => observer.disconnect();
  }, []);

  /* ── Fisheye lens ── */
  const applyLens = useCallback(() => {
    const strip = stripRef.current;
    const bases = basesRef.current;
    const slotList = slotsRef.current;
    if (!strip || bases.length === 0 || !compressedRef.current) return;

    // Focus index in *base* (uniform compressed) coordinates — stable
    // mapping: each slot owns a fixed cursor band, so the lens never
    // oscillates as it redistributes heights around the cursor.
    const rel =
      pointerYRef.current -
      strip.getBoundingClientRect().top -
      STRIP_PADDING_TOP;
    let acc = 0;
    let focus = bases.length - 1;
    for (let i = 0; i < bases.length; i += 1) {
      acc += bases[i]!;
      if (rel <= acc) {
        focus = i;
        break;
      }
    }

    // Grow near the focus toward natural height (Gaussian falloff), then
    // shrink distant slots proportionally to conserve the total height.
    const want = bases.map((base, i) => {
      if (slotList[i]!.isSeparator) return base;
      const d = (i - focus) / SIGMA_SLOTS;
      return base + (NATURAL_SLOT_H - base) * Math.exp(-(d * d) / 2);
    });
    const totalBase = bases.reduce((sum, v) => sum + v, 0);
    const excess = want.reduce((sum, v) => sum + v, 0) - totalBase;
    const shrinkable = want.map((w, i) => {
      const d = Math.abs(i - focus) / SIGMA_SLOTS;
      return Math.max(0, w - MIN_LENS_SLOT_H) * (1 - Math.exp(-(d * d) / 2));
    });
    const shrinkTotal = shrinkable.reduce((sum, v) => sum + v, 0) || 1;

    const els = slotElsRef.current;
    for (let i = 0; i < bases.length; i += 1) {
      const el = els[i];
      if (!el) continue;
      const height = Math.max(
        MIN_LENS_SLOT_H,
        want[i]! - excess * (shrinkable[i]! / shrinkTotal),
      );
      el.style.height = `${height}px`;
      if (i === focus && !slotList[i]!.isSeparator) {
        el.setAttribute("data-lens-hot", "true");
      } else {
        el.removeAttribute("data-lens-hot");
      }
    }

    const focusSlot = slotList[focus]!;
    const focusNodeId = focusSlot.isSeparator ? null : focusSlot.nodeId;
    if (focusNodeId !== focusNodeIdRef.current) {
      focusNodeIdRef.current = focusNodeId;
      onFocusChangeRef.current(focusNodeId);
    }
  }, []);

  const handleMouseMove = useCallback(
    (event: ReactMouseEvent) => {
      if (!compressedRef.current) return;
      pointerYRef.current = event.clientY;
      const strip = stripRef.current;
      if (strip) strip.dataset.lensing = "true";
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        applyLens();
      });
    },
    [applyLens],
  );

  const resetLens = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const strip = stripRef.current;
    if (strip) delete strip.dataset.lensing;
    const bases = basesRef.current;
    slotElsRef.current.forEach((el, i) => {
      if (!el) return;
      el.style.height = `${bases[i] ?? NATURAL_SLOT_H}px`;
      el.removeAttribute("data-lens-hot");
    });
    if (focusNodeIdRef.current !== null) {
      focusNodeIdRef.current = null;
      onFocusChangeRef.current(null);
    }
  }, []);

  // If compression ends (resize, frames removed) while the lens is active,
  // drop the lens so slots return to their natural heights.
  useEffect(() => {
    if (!compressed) resetLens();
  }, [compressed, resetLens]);

  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const registerSlot = useCallback((index: number, el: HTMLElement | null) => {
    slotElsRef.current[index] = el;
  }, []);

  return {
    stripRef,
    registerSlot,
    compressed,
    naturalHeight,
    slotHeights,
    focusNodeIdRef,
    handleMouseMove,
    resetLens,
  };
}
